import React from 'react';
import './About.css';

const IconShield = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const IconHandshake = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 11l-5-5-5 5" />
    <path d="M6 13l6 6 6-6" />
  </svg>
);

const IconChart = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const IconCode = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const About = () => {
  const pillars = [
    {
      number: '01',
      title: 'Honest Communication',
      subtitle: 'No Jargon, Just Clarity',
      description: 'We speak plain English. You will always know what we are building, why we are building it, and how long it will take.',
      icon: <IconHandshake size={24} />,
    },
    {
      number: '02',
      title: 'Built to Last',
      subtitle: 'Clean & Scalable Code',
      description: 'No quick hacks or fragile workarounds. We write clean, tested code that stays fast and easy to maintain as you grow.',
      icon: <IconShield size={24} />,
    },
    {
      number: '03',
      title: 'Direct Engineer Access',
      subtitle: 'No Account Managers',
      description: 'You work directly with the senior developers and designers building your project. Fast feedback, zero middleman lag.',
      icon: <IconCode size={24} />,
    },
    {
      number: '04',
      title: 'Focus on Real Results',
      subtitle: 'Driven by Business Growth',
      description: 'Pretty design is great, but code that brings real leads, sales, and happy users is what actually matters to your business.',
      icon: <IconChart size={24} />,
    },
  ];

  return (
    <section id="about" className="about">
      <div className="about-container">
        <div className="section-header">
          <div className="section-badge">
            <span>Our Story</span>
          </div>
          <h2 className="section-title">Real Engineers. Honest Work. Zero Agency Fluff.</h2>
          <p className="section-subtitle">
            Trevox is an enterprise software team operating in strategic collaboration with MarketIQ Solutions. We help ambitious startups and businesses build reliable, high-performing software.
          </p>
        </div>

        {/* Overview Grid */}
        <div className="about-overview-grid">
          <div className="about-story-card">
            <h3 className="about-story-title">We started Trevox to fix what's broken in tech consulting.</h3>
            <p className="about-story-text">
              Too many traditional agencies charge top dollar for slow progress, endless middle managers, and AI-generated buzzwords. We built Trevox to be different: a lean, dedicated team of engineers who care deeply about craft.
            </p>
            <p className="about-story-text">
              From your initial idea sketch to launch day and beyond, we partner with you like an in-house tech team — focused on quality, speed, and real revenue impact.
            </p>

            <div className="about-stats-row">
              <div className="about-stat-box">
                <span className="stat-value">100%</span>
                <span className="stat-desc">Clean Code Guarantee</span>
              </div>
              <div className="about-stat-box">
                <span className="stat-value">99.8%</span>
                <span className="stat-desc">On-Time Sprint Delivery</span>
              </div>
              <div className="about-stat-box">
                <span className="stat-value">5.0 / 5.0</span>
                <span className="stat-desc">Client Satisfaction</span>
              </div>
            </div>
          </div>

          <div className="about-visual-card">
            <div className="visual-glass-content">
              <div className="visual-logo-badge">
                <img
                  src="/logo.png"
                  alt="Trevox Technologies"
                  className="about-brand-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="visual-quote">
                <div className="quote-mark">“</div>
                <p>We treat every client project with the care, precision, and dedication as if it were our own business.</p>
                <div className="quote-author">— Trevox Engineering Team</div>
              </div>
            </div>
          </div>
        </div>

        {/* Guiding Principles */}
        <div className="pillars-section">
          <div className="pillars-header">
            <h3 className="pillars-main-title">How We Work With You</h3>
            <p className="pillars-main-desc">The core principles behind every line of code we write and every decision we make.</p>
          </div>

          <div className="pillars-grid">
            {pillars.map((pillar, index) => (
              <div key={index} className="pillar-card">
                <div className="pillar-top">
                  <span className="pillar-number">{pillar.number}</span>
                  <div className="pillar-icon-box">{pillar.icon}</div>
                </div>

                <h4 className="pillar-title">{pillar.title}</h4>
                <div className="pillar-subtitle">{pillar.subtitle}</div>
                <p className="pillar-description">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;